import React, { useState } from 'react';
import { motion, AnimatePresence, Reorder, useDragControls } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabaseHelpers } from '../../../lib/supabase';
import { useToast } from '../../../contexts/ToastContext';

const MultiFilamentSelector = ({ selectedFilaments, filaments, projectQuantity, onFilamentAdd, onFilamentUpdate, onFilamentRemove, onFilamentReorder, onFilamentsReorder, onNext }) => {
  const { success, error } = useToast();
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [draggedOver, setDraggedOver] = useState(null);
  const [newFilament, setNewFilament] = useState({
    filamentId: '',
    weight: '',
    waste: ''
  });
  const [weightError, setWeightError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterColor, setFilterColor] = useState('');
  const [filterStock, setFilterStock] = useState(''); // 'high' | 'medium' | 'low'
  const [filterMaterial, setFilterMaterial] = useState('');

  const brands = Array.from(new Set((filaments || []).map(f => f?.brand).filter(Boolean)));
  const colors = Array.from(new Set((filaments || []).map(f => f?.color).filter(Boolean)));
  const colorHexMap = Object.fromEntries(
    (filaments || [])
      .filter(f => f?.color && (f?.color_hex || f?.colorHex))
      .map(f => [f?.color, f?.color_hex || f?.colorHex])
  );

  // Tooltip híbrido: hover no desktop (md:hover) e clique no mobile, com cor destacada
  const InfoTooltip = ({ content }) => {
    const [open, setOpen] = useState(false);
    return (
      <span className="relative inline-flex items-center align-middle group">
        <button
          type="button"
          aria-label="Mais informações"
          onClick={() => setOpen(o => !o)}
          onBlur={() => setOpen(false)}
          className="ml-1 inline-flex"
        >
          <Icon name="HelpCircle" size={14} className="text-muted-foreground hover:text-foreground" />
        </button>
        <div className={`absolute left-0 top-full mt-2 w-64 p-3 rounded-md shadow-elevation-2 text-xs z-50 bg-popover text-popover-foreground border border-border ${open ? 'block' : 'hidden md:group-hover:block'}`}>
          {content}
        </div>
      </span>
    );
  };

  const getStockLevel = (grams) => {
    const g = parseFloat(grams || 0);
    if (g >= 600) return 'high';
    if (g >= 300) return 'medium';
    return 'low';
  };

  const dataMaterials = Array.from(new Set((filaments || []).map(f => f?.material).filter(Boolean)));
  const primaryMaterials = ['PLA', 'PETG'];
  const materials = Array.from(new Set([...primaryMaterials, ...dataMaterials]));

  const filteredFilaments = (filaments || [])
    .filter(f => !selectedFilaments?.find(s => s?.filamentId === f?.id))
    .filter(f => !filterBrand || f?.brand === filterBrand)
    .filter(f => !filterMaterial || f?.material === filterMaterial)
    .filter(f => !filterColor || f?.color === filterColor)
    .filter(f => !filterStock || getStockLevel(f?.stock_grams) === filterStock)
    .filter(f => !searchTerm || (
      (f?.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (f?.color || '').toLowerCase().includes(searchTerm.toLowerCase())
    ));

  // Modal simples de confirmação
  const ConfirmModal = ({ isOpen, onCancel, onConfirm, need, stock, filamentName }) => {
    if (!isOpen) return null;
    return (
      <>
        <div className="fixed inset-0 bg-black/50 z-50 transition-opacity" onClick={onCancel} />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl shadow-elevation-2 w-full max-w-md">
            <div className="flex items-center space-x-3 p-6 border-b border-border">
              <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-warning" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground">Estoque Insuficiente</h3>
                <p className="text-sm text-muted-foreground">Deseja prosseguir mesmo assim?</p>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-foreground mb-3">
                O filamento <span className="font-semibold text-primary">{filamentName}</span> possui estoque insuficiente para a quantidade configurada.
              </p>
              <div className="bg-muted rounded-xl p-3 text-sm">
                Necessário: <span className="font-semibold">{need?.toFixed(1)}g</span> • Disponível: <span className="font-semibold">{stock?.toFixed(1)}g</span>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-6 border-t border-border">
              <Button variant="outline" onClick={onCancel} className="flex-1">Cancelar</Button>
              <Button onClick={onConfirm} className="flex-1" iconName="Check" iconPosition="left">Prosseguir</Button>
            </div>
          </div>
        </div>
      </>
    );
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmInfo, setConfirmInfo] = useState(null);

  const handleAddFilament = () => {
    const w = parseFloat(newFilament?.weight);
    const hasValidWeight = Number.isFinite(w) && w > 0;
    if (!hasValidWeight) {
      setWeightError('Por favor, insira o peso do filamento antes de adicioná-lo');
      try {
        const el = document.getElementById('new-filament-weight');
        el && el.focus();
      } catch {}
      return;
    }
    if (newFilament?.filamentId && hasValidWeight) {
      const filamentData = filaments?.find(f => f?.id === newFilament?.filamentId);
      if (filamentData) {
        onFilamentAdd({
          filamentId: newFilament?.filamentId,
          weight: parseFloat(newFilament?.weight),
          waste: parseFloat(newFilament?.waste || 0),
          filamentData: filamentData
        });
        setNewFilament({ filamentId: '', weight: '', waste: '' });
        setWeightError('');
        setShowAddForm(false);
      }
    }
  };

  const handleDragOver = (e, filamentId) => {
    e?.preventDefault();
    setDraggedOver(filamentId);
  };

  const handleDragLeave = () => {
    setDraggedOver(null);
  };

  const handleDrop = async (e, filamentId) => {
    e?.preventDefault();
    setDraggedOver(null);
    
    const files = e?.dataTransfer?.files;
    if (files?.length > 0) {
      const file = files?.[0];
      if (file?.type?.startsWith('image/')) {
        await handleImageUpload(filamentId, file);
      }
    }
  };

  const handleImageUpload = async (filamentId, file) => {
    if (!file || !filamentId) return;

    setUploadingImage(true);
    try {
      // Upload and get public URL
      const { publicUrl } = await supabaseHelpers?.uploadFilamentImage(file, filamentId);
      if (!publicUrl) throw new Error('Falha ao obter URL pública da imagem.');

      // Compute updated image_urls, prioritizing the newly uploaded image
      const currentFilament = filaments?.find(f => f?.id === filamentId);
      const existing = currentFilament?.image_urls || [];
      const updatedImages = [publicUrl, ...existing]?.slice(0, 5);

      await supabaseHelpers?.updateFilament(filamentId, { image_urls: updatedImages, image: updatedImages?.[0] });
      
      success('Imagem enviada com sucesso!');
      // Reload to reflect changes from database
      window.location?.reload();
    } catch (error) {
      console.error('Erro no upload da imagem:', error);
      error('Erro ao processar imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  if (filaments?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center animate-fade-in">
        <div className="w-20 h-20 bg-gradient-to-br from-secondary/20 to-accent/20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
        <Icon name="Filament" size={40} className="text-secondary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-4">Nenhum Filamento Cadastrado</h3>
        <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
          Você precisa cadastrar pelo menos um filamento para usar a calculadora profissional.
        </p>
        <Button 
          onClick={() => window.location.href = '/filament-inventory'}
          className="flex items-center space-x-3 bg-gradient-to-r from-secondary to-accent px-8 py-4 text-lg font-semibold"
        >
          <Icon name="Plus" size={20} />
          <span>Cadastrar Primeiro Filamento</span>
        </Button>
      </div>
    );
  }

  return (
    <>
    <div className="bg-card border border-border rounded-xl p-8 animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <div className="w-14 h-14 bg-gradient-to-br from-secondary/20 to-accent/20 rounded-xl flex items-center justify-center shadow-lg">
        <Icon name="Filament" size={28} className="text-secondary" />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-foreground">Filamentos do Projeto</h3>
            <p className="text-muted-foreground text-lg">Adicione múltiplos filamentos e suas quantidades</p>
          </div>
        </div>

        <Button
          onClick={() => setShowAddForm(true)}
          className="flex items-center space-x-2 bg-gradient-to-r from-secondary to-accent px-6 py-3 shadow-lg hover:shadow-xl"
          disabled={showAddForm}
        >
          <Icon name="Plus" size={18} />
          <span>Adicionar Filamento</span>
        </Button>
      </div>
      {/* Add Filament Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -20 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -20 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="mb-8 bg-card border border-primary/20 rounded-xl p-6 bg-gradient-to-br from-primary/5 to-secondary/5"
          >
            <h4 className="text-lg font-bold text-foreground mb-4 flex items-center">
              <Icon name="Plus" size={20} className="mr-2 text-primary" />
              Adicionar Novo Filamento ao Projeto
            </h4>
            {/* Entradas principais para cálculo de disponibilidade */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">
                  Peso total do projeto (g)
                  <InfoTooltip content="Peso deste filamento para o projeto inteiro. Informe o material útil; perdas podem ser inseridas abaixo em 'Desperdício (g)'." />
                </label>
                <Input
                  id="new-filament-weight"
                  type="number"
                  inputMode="decimal"
                  placeholder="Ex: 250g"
                  value={newFilament?.weight}
                  onChange={(e) => {
                    const val = e?.target?.value;
                    setNewFilament(prev => ({ ...prev, weight: val }));
                    const num = parseFloat(val);
                    if (Number.isFinite(num) && num > 0) {
                      setWeightError('');
                    }
                  }}
                  min="0.1"
                  step="0.1"
                  aria-invalid={Boolean(weightError)}
                  aria-describedby={weightError ? 'new-filament-weight-error' : undefined}
                  className="text-lg"
                />
                {weightError && (
                  <div
                    id="new-filament-weight-error"
                    role="alert"
                    aria-live="assertive"
                    className="mt-2 p-2 rounded-md bg-destructive/10 border border-destructive/30 text-destructive flex items-center text-sm"
                  >
                    <Icon name="AlertCircle" size={16} className="mr-2" />
                    <span>{weightError}</span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">Refere-se ao total deste filamento no projeto.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">
                  Desperdício (g)
                  <InfoTooltip content="Somente as perdas deste filamento (além do peso da peça): limpeza de bico, brim/raft removidos, suportes descartados, sobras de troca de cor e falhas pequenas." />
                </label>
                <Input
                  type="number"
                  placeholder="Ex: 15"
                  value={newFilament?.waste}
                  onChange={(e) => setNewFilament(prev => ({ ...prev, waste: e?.target?.value }))}
                  min="0"
                  step="0.1"
                  className="text-lg"
                />
                <p className="text-xs text-muted-foreground mt-1">Também é específico deste filamento, por peça.</p>
              </div>
            </div>
            {/* Filtros: melhorar organização (Busca, Marca, Material, Cor, Estoque) */}
            <div className="space-y-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Buscar (cor ou marca)</label>
                  <Input
                    type="text"
                    placeholder="Ex: preto, Prusament"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Marca</label>
                  <select
                    value={filterBrand}
                    onChange={(e) => setFilterBrand(e?.target?.value)}
                    className="w-full px-4 py-3 bg-input border border-border rounded-xl text-foreground"
                  >
                    <option value="">Todas</option>
                    {brands?.map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Material</label>
                  <div className="flex gap-2 flex-wrap md:flex-nowrap overflow-x-auto px-1">
                    <button
                      type="button"
                      onClick={() => setFilterMaterial('')}
                      className={`px-3 py-2 rounded-xl border ${!filterMaterial ? 'border-primary text-primary bg-primary/10' : 'border-border text-foreground bg-input'}`}
                    >
                      Todos
                    </button>
                    {materials?.map(m => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setFilterMaterial(m)}
                        className={`px-3 py-2 rounded-xl border ${filterMaterial === m ? 'border-primary text-primary bg-primary/10' : 'border-border text-foreground bg-input'}`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Cor</label>
                  <div className="flex gap-2 flex-wrap md:flex-nowrap overflow-x-auto">
                    <button
                      type="button"
                      onClick={() => setFilterColor('')}
                      className={`px-3 py-2 rounded-xl border ${!filterColor ? 'border-primary text-primary bg-primary/10' : 'border-border text-foreground bg-input'}`}
                    >
                      Todas
                    </button>
                    {colors?.map(c => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setFilterColor(c)}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-xl border ${filterColor === c ? 'border-primary text-primary bg-primary/10' : 'border-border text-foreground bg-input'}`}
                        title={c}
                      >
                        <span
                          className="w-4 h-4 rounded-full border border-border"
                          style={{ backgroundColor: colorHexMap[c] || '#666666' }}
                        />
                        <span className="text-sm">{c}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Estoque</label>
                  <select
                    value={filterStock}
                    onChange={(e) => setFilterStock(e?.target?.value)}
                    className="w-full px-4 py-3 bg-input border border-border rounded-xl text-foreground"
                  >
                    <option value="">Todos</option>
                    <option value="high">Alto (≥600g)</option>
                    <option value="medium">Médio (≥300g)</option>
                    <option value="low">Baixo (&lt;300g)</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Selecionar Filamento
                </label>
                {(() => {
                  const usedPerUnit = (parseFloat(newFilament?.weight) || 0) + (parseFloat(newFilament?.waste) || 0);
                  const needTotal = usedPerUnit; // projeto inteiro, sem multiplicar pela quantidade
                  const selectOptions = (filteredFilaments || []).map(filament => {
                    const stock = parseFloat(filament?.stock_grams ?? 0);
                    const insuficiente = stock > 0 && needTotal > stock;
                    const priceKg = parseFloat(filament?.price_per_kg || 0);
                    const label = `${filament?.brand} ${filament?.material} ${filament?.color} — R$ ${priceKg.toFixed(2)}/kg — Disponível: ${Number.isFinite(stock) ? stock.toFixed(1) : 0}g`;
                    const badge = insuficiente ? { text: 'Insuficiente', variant: 'error' } : null;
                    return { value: filament?.id, label, badge };
                  });
                  return (
                    <Select
                      options={selectOptions}
                      value={newFilament?.filamentId}
                      onChange={(val) => setNewFilament(prev => ({ ...prev, filamentId: val }))}
                      searchable
                      clearable
                      placeholder="Escolher filamento..."
                    />
                  );
                })()}
              </div>
              <div className="flex items-end space-x-3">
                <Button
                  onClick={() => {
                    const w = parseFloat(newFilament?.weight);
                    const hasValidWeight = Number.isFinite(w) && w > 0;
                    if (!hasValidWeight) {
                      setWeightError('Por favor, insira o peso do filamento antes de adicioná-lo');
                      try {
                        const el = document.getElementById('new-filament-weight');
                        el && el.focus();
                      } catch {}
                      return;
                    }
                    if (!newFilament?.filamentId) return;
                    const f = filaments?.find(x => x?.id === newFilament?.filamentId);
                    const used = (parseFloat(newFilament?.weight) || 0) + (parseFloat(newFilament?.waste) || 0);
                    const need = used; // projeto inteiro, sem multiplicar pela quantidade
                    const stock = parseFloat(f?.stock_grams ?? 0);
                    if (stock > 0 && need > stock) {
                      setConfirmInfo({ need, stock, filamentName: `${f?.brand} ${f?.material} ${f?.color}` });
                      setConfirmOpen(true);
                    } else {
                      handleAddFilament();
                    }
                  }}
                  disabled={!newFilament?.filamentId}
                  className="flex-1 bg-gradient-to-r from-primary to-secondary"
                >
                  <Icon name="Check" size={16} className="mr-2" />
                  Adicionar
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewFilament({ filamentId: '', weight: '' });
                  }}
                >
                  Cancelar
                </Button>
              </div>

              {/* Alerta de estoque insuficiente */}
              {newFilament?.filamentId && newFilament?.weight && (() => {
                const f = filaments?.find(x => x?.id === newFilament?.filamentId);
                const qty = parseInt(projectQuantity) || 1;
                const used = (parseFloat(newFilament?.weight) || 0) + (parseFloat(newFilament?.waste) || 0);
                const need = used * qty;
                const stock = parseFloat(f?.stock_grams ?? 0);
                if (stock > 0 && need > stock) {
                  return (
                    <div className="mt-3 p-3 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive text-sm">
                      Estoque insuficiente para este filamento: necessário {need.toFixed(1)}g, disponível {stock.toFixed(1)}g.
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Resumo Rápido dos Filamentos (topo) */}
      {selectedFilaments?.length > 0 && (() => {
        const totalWeightUnits = selectedFilaments?.reduce((sum, s) => sum + (parseFloat(s?.weight || 0) || 0), 0);
        const totalWasteUnits = selectedFilaments?.reduce((sum, s) => sum + (parseFloat(s?.waste || 0) || 0), 0);
        const totalWeightGrams = (totalWeightUnits || 0); // por impressão
        const totalWasteGrams = (totalWasteUnits || 0); // por impressão
        const totalMaterialCost = selectedFilaments?.reduce((sum, s) => {
          const fd = filaments?.find(f => f?.id === s?.filamentId);
          const priceKg = parseFloat(fd?.price_per_kg || 0);
          const grams = (parseFloat(s?.weight || 0) + parseFloat(s?.waste || 0)) / 1000;
          return sum + grams * priceKg;
        }, 0) || 0;
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-card border border-border/50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="text-sm font-semibold text-foreground">Peso total da impressão</h5>
                  <p className="text-xs text-muted-foreground">Somatório dos filamentos selecionados</p>
                </div>
                <div className="text-xl font-bold text-foreground">{totalWeightGrams?.toFixed(1)}g</div>
              </div>
            </div>
            <div className="bg-card border border-border/50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="text-sm font-semibold text-foreground">Desperdício</h5>
                  <p className="text-xs text-muted-foreground">Total das perdas</p>
                </div>
                <div className="text-xl font-bold text-destructive">{totalWasteGrams?.toFixed(1)}g</div>
              </div>
            </div>
            <div className="bg-card border border-border/50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="text-sm font-semibold text-foreground">Custo dos Filamentos</h5>
                  <p className="text-xs text-muted-foreground">Material + desperdício</p>
                </div>
                <div className="text-xl font-bold text-primary">R$ {totalMaterialCost?.toFixed(2)}</div>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Selected Filaments List */}
      {selectedFilaments?.length > 0 ? (
        <div className="space-y-6 mb-8">
          <h4 className="text-lg font-bold text-foreground flex items-center">
            <Icon name="Layers" size={20} className="mr-2 text-primary" />
            Filamentos Selecionados ({selectedFilaments?.length})
          </h4>
          <div className="text-xs text-muted-foreground flex items-center space-x-2">
            <Icon name="GripVertical" size={14} />
            <span>Arraste pelo ícone para reordenar a lista</span>
          </div>

          {/* Reorderable list with explicit drag handle */}
          <Reorder.Group axis="y" values={selectedFilaments || []} onReorder={(newOrder) => onFilamentsReorder?.(newOrder)}>
            {selectedFilaments?.map((item, index) => {
              const FilamentRow = () => {
                const dragControls = useDragControls();
                const filamentData = filaments?.find(f => f?.id === item?.filamentId);
                if (!filamentData) return null;
                const totalWeight = (parseFloat(item?.weight || 0) + parseFloat(item?.waste || 0));
                const cost = (totalWeight / 1000) * (filamentData?.price_per_kg || 0);
                return (
                  <Reorder.Item
                    value={item}
                    key={`${item?.filamentId}-${index}`}
                    dragControls={dragControls}
                    dragListener={false}
                    dragElastic={0.08}
                    whileDrag={{ scale: 1.02, opacity: 0.95, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
                    dragTransition={{ bounceStiffness: 130, bounceDamping: 16 }}
                  >
                    <motion.div
                      layout
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ type: 'spring', stiffness: 170, damping: 18, mass: 0.6 }}
                      className="bg-card border border-border/50 rounded-xl p-6 relative hover:shadow-elevation-2 hover:border-primary/30"
                    >
                      {/* Drag handle no canto superior esquerdo do card */}
                      <button
                        type="button"
                        aria-label="Reordenar"
                        onPointerDown={(e) => dragControls.start(e)}
                        className="absolute top-3 left-3 p-1.5 rounded-md bg-muted/60 border border-border hover:bg-muted cursor-grab active:cursor-grabbing"
                      >
                        <Icon name="GripVertical" size={16} className="text-muted-foreground" />
                      </button>
                      <div className="flex items-center space-x-6">
                        {/* Swatch + order number + drag handle */}
                        <motion.div
                          layout
                          className={`
                            w-16 h-16 rounded-full border-2 border-border shadow-lg flex items-center justify-center relative overflow-visible
                            ${draggedOver === filamentData?.id ? 'ring-2 ring-primary/60 ring-offset-2 ring-offset-background' : ''}
                          `}
                          style={{ backgroundColor: filamentData?.color_hex || filamentData?.colorHex || '#666666' }}
                          transition={{ type: 'spring', stiffness: 160, damping: 22, mass: 0.6 }}
                        >
                          <div className="absolute -top-3 -left-3 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shadow ring-1 ring-border">
                            {index + 1}
                          </div>
                        </motion.div>

                        {/* Filament Details */}
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-bold text-foreground text-xl">
                              {filamentData?.brand} {filamentData?.material}
                            </h4>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-muted-foreground">
                                R$ {filamentData?.price_per_kg?.toFixed(2)}/kg
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="flex items-center space-x-3">
                              <span className="text-sm text-muted-foreground font-medium">Cor:</span>
                              <span className="text-sm font-semibold text-foreground">
                                {filamentData?.color}
                              </span>
                            </div>

                            <div className="flex items-center space-x-3">
                              <span className="text-sm text-muted-foreground font-medium">
                                Peso total do projeto (g)
                                <InfoTooltip content="Peso deste filamento para o projeto inteiro. Informe o material útil; perdas podem ser inseridas em 'Desperdício (g)'." />
                              </span>
                              <div className="flex items-center space-x-2">
                                <Input
                                  type="number"
                                  value={item?.weight}
                                  onChange={(e) => onFilamentUpdate(index, { weight: parseFloat(e?.target?.value) || 0 })}
                                  className="w-20 text-sm font-semibold"
                                  min="0"
                                  step="0.1"
                                />
                                <span className="text-sm text-muted-foreground">g</span>
                              </div>
                            </div>

                            <div className="flex items-center space-x-3">
                              <span className="text-sm text-muted-foreground font-medium">
                                Desperdício (g)
                                <InfoTooltip content="Somente as perdas do processo (além do peso da peça): limpeza de bico, brim/raft removidos, suportes descartados, sobras de troca de cor e falhas pequenas." />
                              </span>
                              <div className="flex items-center space-x-2">
                                <Input
                                  type="number"
                                  value={item?.waste || 0}
                                  onChange={(e) => onFilamentUpdate(index, { waste: parseFloat(e?.target?.value) || 0 })}
                                  className="w-20 text-sm font-semibold"
                                  min="0"
                                  step="0.1"
                                />
                                <span className="text-sm text-muted-foreground">g</span>
                              </div>
                            </div>

                            <div className="flex items-center space-x-3">
                              <span className="text-sm text-muted-foreground font-medium">Custo:</span>
                              <span className="text-xl font-bold text-primary">
                                R$ {cost?.toFixed(2)}
                              </span>
                            </div>
                          </div>

                          {(() => {
                            const wasteGrams = parseFloat(item?.waste || 0);
                            const pricePerKg = (filamentData?.price_per_kg || 0);
                            const pricePerGram = pricePerKg / 1000;
                            const wasteCostUnit = wasteGrams * pricePerGram;
                            return (
                              <div className="mt-2 text-sm text-muted-foreground">
                                Custo de desperdício: <span className="font-semibold text-destructive">R$ {wasteCostUnit?.toFixed(2)}</span>
                              </div>
                            );
                          })()}

                          {(() => {
                            const qty = parseInt(projectQuantity) || 1;
                            const need = totalWeight * qty;
                            const stock = parseFloat(filamentData?.stock_grams ?? 0);
                            const remain = stock > 0 ? Math.max(0, stock - need) : null;
                            return (
                              <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                                <div className="flex items-center space-x-2">
                                  <span className="text-muted-foreground font-medium">SKU:</span>
                                  <span className="font-semibold text-foreground">{filamentData?.sku || '—'}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-muted-foreground font-medium">Disponível:</span>
                                  <span className="font-semibold text-foreground">{Number.isFinite(stock) && stock > 0 ? `${stock.toFixed(1)}g` : '—'}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-muted-foreground font-medium">Necessário:</span>
                                  <span className={`font-semibold ${stock > 0 && need > stock ? 'text-destructive' : 'text-foreground'}`}>{need.toFixed(1)}g</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-muted-foreground font-medium">Sobrará:</span>
                                  <span className="font-semibold text-foreground">{remain !== null ? `${remain.toFixed(1)}g` : '—'}</span>
                                </div>
                              </div>
                            );
                          })()}

                          {(() => {
                            const qty = parseInt(projectQuantity) || 1;
                            const need = totalWeight * qty;
                            const stock = parseFloat(filamentData?.stock_grams ?? 0);
                            if (stock > 0 && need > stock) {
                              return (
                                <div className="mt-3 p-2 rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-xs">
                                  Este filamento pode estar insuficiente para a quantidade: necessário {need.toFixed(1)}g, disponível {stock.toFixed(1)}g.
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>

                        {/* Remove */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onFilamentRemove(index)}
                          className="text-destructive hover:bg-destructive/10 hover:scale-110 transition-all"
                        >
                          <Icon name="Trash2" size={18} />
                        </Button>
                      </div>
                    </motion.div>
                  </Reorder.Item>
                );
              };

              return <FilamentRow key={`${item?.filamentId}-${index}`} />;
            })}
          </Reorder.Group>
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground animate-fade-in">
        <Icon name="Filament" size={64} className="mx-auto mb-6 opacity-50" />
          <h4 className="text-xl font-semibold mb-2">Nenhum filamento selecionado</h4>
          <p className="text-lg">Clique em "Adicionar Filamento" para começar</p>
        </div>
      )}
      {/* Continue Button removido: usaremos apenas o botão externo de navegação de etapas */}
      {/* Upload Loading */}
      {uploadingImage && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-xl p-8 flex items-center space-x-4 shadow-2xl">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <div>
              <span className="text-foreground text-lg font-semibold">Enviando imagem...</span>
              <p className="text-muted-foreground text-sm">Processando upload do arquivo</p>
            </div>
          </div>
        </div>
      )}
    </div>
    {/* Modal de confirmação para estoque insuficiente */}
    <ConfirmModal
      isOpen={confirmOpen}
      onCancel={() => setConfirmOpen(false)}
      onConfirm={() => { setConfirmOpen(false); handleAddFilament(); }}
      need={confirmInfo?.need}
      stock={confirmInfo?.stock}
      filamentName={confirmInfo?.filamentName}
    />
    </>
  );
};

export default MultiFilamentSelector;